import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Text, VStack, Spinner } from '@chakra-ui/react';
import { ShieldX, UserX } from 'lucide-react';
import { auth, db } from '../../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { POKER_COLORS } from '../../theme/designSystem';

interface AgentRouteProps {
  children: React.ReactNode;
}

/**
 * AgentRoute - Protected route that only allows agent access
 * 
 * Checks if the current user exists in the 'agents' collection
 * and has active: true
 */
const AgentRoute: React.FC<AgentRouteProps> = ({ children }) => {
  const [isAgent, setIsAgent] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [agentData, setAgentData] = useState<any>(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setIsAgent(false);
        setLoading(false);
        return;
      }

      try {
        // Check if user is in agents collection
        const agentDoc = await getDoc(doc(db, 'agents', user.uid));
        
        if (agentDoc.exists()) {
          const data = agentDoc.data();
          if (data.active === true) {
            setIsAgent(true);
            setAgentData(data);
          } else {
            // Agent exists but is deactivated
            setIsAgent(false);
            setAgentData({ deactivated: true });
          }
        } else {
          setIsAgent(false);
        }
      } catch (error) {
        console.error('Error checking agent status:', error);
        setIsAgent(false);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  if (loading) {
    return (
      <Box
        w="100%"
        h="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="#0D1B2A"
      >
        <VStack spacing={4}>
          <Spinner size="xl" color={POKER_COLORS.cyan} thickness="4px" />
          <Text fontSize="lg" color={POKER_COLORS.cyan}>
            Verifying agent access...
          </Text>
        </VStack>
      </Box>
    );
  }

  // Not authenticated - redirect to login
  if (isAgent === null) {
    return <Navigate to="/login" replace />;
  }

  // Not an agent - show access denied
  if (!isAgent) {
    return (
      <Box
        w="100%"
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="#0D1B2A"
        p={8}
      >
        <VStack spacing={6} textAlign="center">
          <Box
            p={4}
            borderRadius="full"
            bg={`${POKER_COLORS.darkRed}22`}
            border={`2px solid ${POKER_COLORS.darkRed}`}
          >
            {agentData?.deactivated ? (
              <UserX size={48} color={POKER_COLORS.darkRed} />
            ) : (
              <ShieldX size={48} color={POKER_COLORS.darkRed} />
            )}
          </Box>
          
          <Text color={POKER_COLORS.darkRed} fontSize="2xl" fontWeight="bold">
            {agentData?.deactivated ? 'Agent Access Suspended' : 'Agent Access Required'}
          </Text>
          
          <Text color="gray.400" maxW="400px">
            {agentData?.deactivated 
              ? 'Your agent account has been deactivated. Please contact support for more information.'
              : 'This area is restricted to designated agents only. If you believe you should have access, please contact support.'}
          </Text>

          <VStack spacing={2} pt={4}>
            <Text
              color={POKER_COLORS.cyan}
              fontSize="sm"
              cursor="pointer"
              onClick={() => window.location.href = '/'}
              _hover={{ textDecoration: 'underline' }}
            >
              ← Return to Home
            </Text>
            
            <Text color="gray.500" fontSize="xs">
              Want to become an agent?{' '}
              <Text
                as="span"
                color={POKER_COLORS.gold}
                cursor="pointer"
                _hover={{ textDecoration: 'underline' }}
                onClick={() => window.location.href = '/profile'}
              >
                Contact us
              </Text>
            </Text>
          </VStack>
        </VStack>
      </Box>
    );
  }

  // Is agent - render children
  return <>{children}</>;
};

export default AgentRoute;

/**
 * Helper function to check if current user is an agent
 * Can be used in components to conditionally show agent features
 */
export const checkIsAgent = async (): Promise<boolean> => {
  const currentUser = auth.currentUser;
  if (!currentUser) return false;

  try {
    const agentDoc = await getDoc(doc(db, 'agents', currentUser.uid));
    return agentDoc.exists() && agentDoc.data().active === true;
  } catch (error) {
    console.error('Error checking agent status:', error);
    return false;
  }
};

/**
 * Hook-style checker for agent status (non-reactive)
 */
export const isCurrentUserAgent = async (): Promise<{
  isAgent: boolean;
  agentData?: any;
}> => {
  const currentUser = auth.currentUser;
  if (!currentUser) return { isAgent: false };

  try {
    const agentDoc = await getDoc(doc(db, 'agents', currentUser.uid));
    if (agentDoc.exists() && agentDoc.data().active === true) {
      return { isAgent: true, agentData: agentDoc.data() };
    }
    return { isAgent: false };
  } catch (error) {
    console.error('Error checking agent status:', error);
    return { isAgent: false };
  }
};